import type { Ref } from 'vue'
import { useNuxtApp } from '#imports'

interface Auth0Client {
  getAccessTokenSilently(): Promise<string>
  isAuthenticated: Ref<boolean>
}

/**
 * Auth Adapter - Abstracts authentication across different Auth0 modules
 * Works with both nuxt-auth0 (console) and nuxt-auth-utils (admin)
 */

const getAuth0Client = (
  nuxtApp: ReturnType<typeof useNuxtApp>,
): Auth0Client | null => {
  const auth0 = (nuxtApp as Record<string, unknown>).$auth0
  if (auth0 && typeof auth0 === 'function') {
    return auth0() as Auth0Client
  }
  return null
}

const getTokenFromAuth0 = async (
  client: Auth0Client,
): Promise<string | null> => {
  try {
    return await client.getAccessTokenSilently()
  } catch {
    return null
  }
}

const getTokenFromSession = async (
  nuxtApp: ReturnType<typeof useNuxtApp>,
): Promise<string | null> => {
  try {
    const $fetch = (nuxtApp as Record<string, unknown>).$fetch
    if (!$fetch || typeof $fetch !== 'function') return null

    const response = await $fetch('/api/auth/session')
    const session = response?.data ?? response
    return session?.accessToken ?? null
  } catch {
    return null
  }
}

export const useAuthAdapter = () => {
  const nuxtApp = useNuxtApp()

  const getAccessToken = async (): Promise<string> => {
    const auth0Client = getAuth0Client(nuxtApp)
    if (auth0Client) {
      const token = await getTokenFromAuth0(auth0Client)
      if (token) return token
    }

    const token = await getTokenFromSession(nuxtApp)
    if (token) return token

    throw new Error('No JWT token available')
  }

  const isAuthenticated = async (): Promise<boolean> => {
    const auth0Client = getAuth0Client(nuxtApp)
    if (auth0Client?.isAuthenticated?.value) {
      return true
    }

    // Fallback to session token
    const sessionToken = await getTokenFromSession(nuxtApp)
    return sessionToken !== null
  }

  return {
    getAccessToken,
    isAuthenticated,
  }
}
